import { HandleReturnItemChange } from 'components/Orders/Order';
import { ReasonCode, ReturnItem, ReturnOrderLine } from 'returnCenter-api';
import { useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { StyledSelect } from './RefundCell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen } from '@fortawesome/pro-solid-svg-icons';
import Flex from 'components/ui/Flex';
import { useTranslation } from 'react-i18next';

type IntercompanyReasonCodeCellProps = {
    item: ReturnItem;
    itemId?: number | string;
    handleReturnItemChange: HandleReturnItemChange;
    orderline?: ReturnOrderLine;
    disabled?: boolean;

    /** The supplying company's reason codes — not the selling company's. */
    reasonCodes: ReasonCode[];

    /** What the row inherits when the operator has not taken it over: the customer row's own code. */
    inheritedReasonCode?: number | null;
};

/**
 * Which reason the supplying company is told about, alongside the decision code that says how much
 * they are credited.
 *
 * A reason code does not travel between group companies as a matter of course — the sets overlap
 * rather than match — and an unknown one leaves the receiving company's rules with nothing to key
 * on. The lock works as on the decision code: unlocked is the operator's, and nothing moves it.
 */
const IntercompanyReasonCodeCell = ({
    item,
    itemId,
    handleReturnItemChange,
    orderline,
    disabled = false,
    reasonCodes,
    inheritedReasonCode,
}: IntercompanyReasonCodeCellProps) => {
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);

    const isManual = item.intercompanyReasonCodeIsManual === true;
    const isLocked = !isManual;

    const options = useMemo(
        () =>
            (reasonCodes ?? []).map((code: ReasonCode) => ({
                value: code.id,
                label: code.name ?? `${code.id}`,
            })),
        [reasonCodes],
    );

    const currentValue = useMemo(() => {
        const effective = isManual ? item.intercompanyReasonCode : inheritedReasonCode;
        if (effective === null || effective === undefined || effective === 0) return null;

        // A code the supplying company does not have has no option to show, so name it by number —
        // that is the state the operator is here to resolve.
        return (
            options.find((option) => option.value === effective) ?? {
                value: effective,
                label: `${effective}`,
            }
        );
    }, [options, isManual, item.intercompanyReasonCode, inheritedReasonCode]);

    const change = (
        property: 'intercompanyReasonCode' | 'intercompanyReasonCodeIsManual',
        value: number | boolean,
    ) =>
        handleReturnItemChange(
            orderline?.orderNumber ?? '',
            orderline?.sku ?? '',
            orderline?.orderLineNumber ?? '',
            itemId ?? -1,
            property,
            value,
        );

    return (
        <Wrapper $grow={1} align="center" gap="0.5rem">
            <StyledSelect
                isDisabled={disabled || isLocked}
                data-item-id={itemId}
                key={itemId}
                menuIsOpen={menuOpen}
                onMenuOpen={() => setMenuOpen(true)}
                onMenuClose={() => setMenuOpen(false)}
                defaultValue={null}
                value={currentValue}
                onChange={(selected: any) => {
                    change('intercompanyReasonCode', (selected as { value: number }).value);
                    setMenuOpen(false);
                }}
                options={options}
                menuPortalTarget={document.body}
            />
            <LockButton
                onClick={() => change('intercompanyReasonCodeIsManual', !isManual)}
                disabled={disabled}
                $isLocked={isLocked}
                type="button"
                title={
                    isLocked
                        ? t('intercompany.unlockToEdit', 'Unlock to edit manually')
                        : t('intercompany.lockToUseAutomatic', 'Lock to use automatic value')
                }
            >
                <FontAwesomeIcon icon={isLocked ? faLock : faLockOpen} />
            </LockButton>
        </Wrapper>
    );
};

export default IntercompanyReasonCodeCell;

const Wrapper = styled(Flex)``;

const LockButton = styled.button<{ $isLocked: boolean }>`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    color: ${({ $isLocked, theme }) => ($isLocked ? theme.colors.textLight : theme.colors.accent)};
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.7;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
        opacity: 1;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.3;
    }
`;
